import React, { useState, useEffect } from 'react';
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Alert,
    CircularProgress,
    Typography,
    Divider,
    useMediaQuery,
    useTheme as useMuiTheme
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
    createRider,
    updateRider,
    getCities,
    Rider,
    CreateRiderData,
    UpdateRiderData,
} from '../../store/slices/riderSlice';
import { useTheme } from '../../contexts/ThemeContext';
import GenericModal from '../../components/GenericModal';
import GenericButton from '../../components/GenericButton';

interface RiderFormProps {
    open: boolean;
    rider?: Rider | null;
    onClose: () => void;
    onSuccess: () => void;
}

const RiderForm: React.FC<RiderFormProps> = ({ open, rider, onClose, onSuccess }) => {
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const dispatch = useDispatch<AppDispatch>();
    const { cities, loading, error } = useSelector((state: RootState) => state.riders);

    // Form state
    const [formData, setFormData] = useState<CreateRiderData>({
        name: '',
        email: '',
        password: '',
        cnic: '',
        gender: '',
        phone: '',
        phone2: '',
        dob: '',
        language: '',
        city_id: 0,
        address: '',
        capacity_in_unit: '',
        capacity_in_weight: '',
        capacity_in_volume: '',
        vehicle_number: '',
        weekly_of_day: '',
        distance_allowed: '',
        license_validity: '',
        shift_timing_from: '',
        shift_timing_to: '',
        break_timing_from: '',
        break_timing_to: '',
    });

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Load cities when modal opens
    useEffect(() => {
        if (open) {
            dispatch(getCities());
        }
    }, [open, dispatch]);

    // Initialize form with rider data if editing
    useEffect(() => {
        if (rider) {
            setFormData({
                name: rider.name,
                email: rider.email,
                password: '', // Don't populate password for editing
                cnic: rider.profile?.cnic || '',
                gender: rider.profile?.gender || '',
                phone: rider.profile?.phone || '',
                phone2: rider.profile?.phone2 || '',
                dob: rider.profile?.dob || '',
                language: rider.profile?.language || '',
                city_id: rider.profile?.city_id || 0,
                address: rider.profile?.address || '',
                capacity_in_unit: rider.rider?.capacity_in_unit || '',
                capacity_in_weight: rider.rider?.capacity_in_weight || '',
                capacity_in_volume: rider.rider?.capacity_in_volume || '',
                vehicle_number: rider.rider?.vehicle_number || '',
                weekly_of_day: rider.rider?.weekly_of_day || '',
                distance_allowed: rider.rider?.distance_allowed || '',
                license_validity: rider.rider?.license_validity || '',
                shift_timing_from: rider.rider?.shift_timing_from || '',
                shift_timing_to: rider.rider?.shift_timing_to || '',
                break_timing_from: rider.rider?.break_timing_from || '',
                break_timing_to: rider.rider?.break_timing_to || '',
            });
        } else {
            setFormData({
                name: '',
                email: '',
                password: '',
                cnic: '',
                gender: '',
                phone: '',
                phone2: '',
                dob: '',
                language: '',
                city_id: 0,
                address: '',
                capacity_in_unit: '',
                capacity_in_weight: '',
                capacity_in_volume: '',
                vehicle_number: '',
                weekly_of_day: '',
                distance_allowed: '',
                license_validity: '',
                shift_timing_from: '',
                shift_timing_to: '',
                break_timing_from: '',
                break_timing_to: '',
            });
        }
        setFormErrors({});
    }, [rider]);

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        if (!rider && !formData.password.trim()) {
            errors.password = 'Password is required';
        } else if (formData.password && formData.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }

        if (!formData.cnic.trim()) {
            errors.cnic = 'CNIC is required';
        }

        if (!formData.gender) {
            errors.gender = 'Gender is required';
        }

        if (!formData.phone.trim()) {
            errors.phone = 'Phone number is required';
        }

        if (!formData.dob) {
            errors.dob = 'Date of birth is required';
        }

        if (!formData.city_id) {
            errors.city_id = 'City is required';
        }

        if (!formData.capacity_in_unit?.trim()) {
            errors.capacity_in_unit = 'Capacity in unit is required';
        }

        if (formData.license_validity && !isValidDate(formData.license_validity)) {
            errors.license_validity = 'Invalid date format';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const isValidDate = (dateString: string): boolean => {
        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date.getTime());
    };

    const handleInputChange = (field: keyof CreateRiderData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            if (rider) {
                // Update existing rider
                const updateData: UpdateRiderData = { ...formData };
                // Remove password if not provided
                if (!updateData.password) {
                    delete updateData.password;
                }
                await dispatch(updateRider({ id: rider.id, data: updateData })).unwrap();
            } else {
                // Create new rider
                await dispatch(createRider(formData)).unwrap();
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving rider:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <GenericModal
            open={open}
            onClose={handleClose}
            title={rider ? 'Edit Rider' : 'Add New Rider'}
            actions={
                <>
                    <GenericButton
                        variant="outline"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        fullWidth={isMobile}
                    >
                        Cancel
                    </GenericButton>
                    <GenericButton
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting || loading}
                        fullWidth={isMobile}
                        onClick={handleSubmit}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            rider ? 'Update Rider' : 'Create Rider'
                        )}
                    </GenericButton>
                </>
            }
        >
            <form onSubmit={handleSubmit}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Basic Information */}
                <Typography variant="h6" gutterBottom>
                    Basic Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Full Name *"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            error={!!formErrors.name}
                            helperText={formErrors.name}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Email Address *"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            error={!!formErrors.email}
                            helperText={formErrors.email}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label={rider ? "Password (leave blank to keep current)" : "Password *"}
                            type="password"
                            value={formData.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            error={!!formErrors.password}
                            helperText={formErrors.password}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="CNIC *"
                            value={formData.cnic}
                            onChange={(e) => handleInputChange('cnic', e.target.value)}
                            error={!!formErrors.cnic}
                            helperText={formErrors.cnic}
                            placeholder="1234567890123"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth error={!!formErrors.gender}>
                            <InputLabel>Gender *</InputLabel>
                            <Select
                                value={formData.gender}
                                onChange={(e) => handleInputChange('gender', e.target.value)}
                                label="Gender *"
                            >
                                <MenuItem value="">Select Gender</MenuItem>
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                            </Select>
                            {formErrors.gender && (
                                <Typography variant="caption" color="error">
                                    {formErrors.gender}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Date of Birth *"
                            type="date"
                            value={formData.dob}
                            onChange={(e) => handleInputChange('dob', e.target.value)}
                            error={!!formErrors.dob}
                            helperText={formErrors.dob}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Contact Information */}
                <Typography variant="h6" gutterBottom>
                    Contact Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Phone Number *"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            error={!!formErrors.phone}
                            helperText={formErrors.phone}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Secondary Phone"
                            value={formData.phone2}
                            onChange={(e) => handleInputChange('phone2', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth error={!!formErrors.city_id}>
                            <InputLabel>City *</InputLabel>
                            <Select
                                value={formData.city_id}
                                onChange={(e) => handleInputChange('city_id', e.target.value)}
                                label="City *"
                            >
                                <MenuItem value={0}>Select City</MenuItem>
                                {cities.map((city) => (
                                    <MenuItem key={city.id} value={city.id}>
                                        {city.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formErrors.city_id && (
                                <Typography variant="caption" color="error">
                                    {formErrors.city_id}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Language"
                            value={formData.language}
                            onChange={(e) => handleInputChange('language', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Address"
                            multiline
                            rows={3}
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Rider Information */}
                <Typography variant="h6" gutterBottom>
                    Rider Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Capacity (Units) *"
                            value={formData.capacity_in_unit}
                            onChange={(e) => handleInputChange('capacity_in_unit', e.target.value)}
                            error={!!formErrors.capacity_in_unit}
                            helperText={formErrors.capacity_in_unit}
                            placeholder="e.g., 50"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Capacity (Weight)"
                            value={formData.capacity_in_weight}
                            onChange={(e) => handleInputChange('capacity_in_weight', e.target.value)}
                            placeholder="e.g., 100kg"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Capacity (Volume)"
                            value={formData.capacity_in_volume}
                            onChange={(e) => handleInputChange('capacity_in_volume', e.target.value)}
                            placeholder="e.g., 10m³"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Vehicle Number"
                            value={formData.vehicle_number}
                            onChange={(e) => handleInputChange('vehicle_number', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Weekly Off Day"
                            value={formData.weekly_of_day}
                            onChange={(e) => handleInputChange('weekly_of_day', e.target.value)}
                            placeholder="e.g., Sunday"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Distance Allowed"
                            value={formData.distance_allowed}
                            onChange={(e) => handleInputChange('distance_allowed', e.target.value)}
                            placeholder="e.g., 50km"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="License Validity"
                            type="date"
                            value={formData.license_validity}
                            onChange={(e) => handleInputChange('license_validity', e.target.value)}
                            error={!!formErrors.license_validity}
                            helperText={formErrors.license_validity}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Shift Information */}
                <Typography variant="h6" gutterBottom>
                    Shift Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Shift Start Time"
                            type="time"
                            value={formData.shift_timing_from}
                            onChange={(e) => handleInputChange('shift_timing_from', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Shift End Time"
                            type="time"
                            value={formData.shift_timing_to}
                            onChange={(e) => handleInputChange('shift_timing_to', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Break Start Time"
                            type="time"
                            value={formData.break_timing_from}
                            onChange={(e) => handleInputChange('break_timing_from', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Break End Time"
                            type="time"
                            value={formData.break_timing_to}
                            onChange={(e) => handleInputChange('break_timing_to', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>

            </form>
        </GenericModal>
    );
};

export default RiderForm;
